"use client";

import { c as _c } from "react/compiler-runtime";
import { CheckCircleIcon } from "@phosphor-icons/react";
import { CheckCircle2, CircleAlert, CircleDashed, Clock3, ListChecks } from "lucide-react";
import { Badge } from "@/src/components/shadcn/ui/badge";
import { ValerieCard } from "@/src/components/valerie-card";
import { TaskStepStatus, type TaskStep } from "@/src/models/task-step";
import type { ReactNode } from "react";
export type ReferralWorkflowStatus = "automated" | "verified" | "verify" | "needs_action" | "failed" | "waiting" | "not_run" | "not_needed";

/** Temporary Confluent copy of the current TPR presentation, pending the shared TPR refactor. */
export type ReferralWorkflowStep = {
  title: string;
  status: ReferralWorkflowStatus;
  summary: string;
  content?: ReactNode;
  continuation?: {
    instruction: string;
    control: ReactNode;
  };
};
export function stepStatus(step: TaskStep | undefined, confirmed: boolean, needsAction = false, skipped = false): ReferralWorkflowStatus {
  if (!step) return "not_run";
  if (step.status === TaskStepStatus.FAILED) return "failed";
  if (needsAction) return "needs_action";
  // Referral steps only pause on automatic timed holds, so a paused step is waiting rather than blocked.
  if (step.status === TaskStepStatus.ASYNC || step.status === TaskStepStatus.PAUSED) return "waiting";
  // Early exits are also persisted as completed. Require success evidence before showing the checkmark.
  if (step.status === TaskStepStatus.COMPLETED && confirmed) return skipped ? "not_needed" : "automated";
  return "needs_action";
}
export function outputId(step: TaskStep | undefined, key: string): string | null {
  const value = step?.output?.[key];
  return typeof value === "string" && value.trim() ? value : null;
}
export function recordOrigin(created: unknown): string {
  return created === true ? "New" : created === false ? "Existing" : "Not recorded";
}
export type DecisionMetadataItem = {
  label: string;
  value: ReactNode;
};
export function ReferralWorkflowLayout(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="flex w-full max-w-2xl flex-col gap-8 sm:min-w-[800px] mx-auto">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
export function DecisionMetadata(t0) {
  const $ = _c(4);
  const {
    items
  } = t0;
  let t1;
  if ($[0] !== items) {
    t1 = items.map(_temp);
    $[0] = items;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <dl className="flex flex-wrap gap-2">{t1}</dl>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp(item) {
  return <div key={item.label} className="inline-flex items-center gap-1.5 rounded-md border bg-muted/30 px-2 py-1 text-xs"><dt className="text-muted-foreground">{item.label}</dt><dd className="font-medium text-foreground">{item.value}</dd></div>;
}
const STATUS_PRESENTATION = {
  automated: {
    label: "Automated",
    className: "border-success-foreground/30 bg-success-foreground/10 text-success-foreground",
    icon: <CheckCircle2 />
  },
  verified: {
    label: "Verified",
    className: "border-success-foreground/30 bg-success-foreground/10 text-success-foreground",
    icon: <CheckCircle2 />
  },
  verify: {
    label: "Verify",
    className: "border-warn/30 bg-warn/10 text-warn-foreground",
    icon: <CircleAlert />
  },
  needs_action: {
    label: "Needs action",
    className: "border-warn-foreground/30 bg-warn-foreground/10 text-warn-foreground",
    icon: <CircleAlert />
  },
  failed: {
    label: "Failed",
    className: "border-destructive-foreground/30 bg-destructive-foreground/10 text-destructive-foreground",
    icon: <CircleAlert />
  },
  waiting: {
    label: "Waiting",
    className: "border-border bg-muted text-muted-foreground",
    icon: <Clock3 />
  },
  not_run: {
    label: "Not run",
    className: "border-border bg-muted text-muted-foreground",
    icon: <CircleDashed />
  },
  not_needed: {
    label: "Not needed",
    className: "border-border bg-muted text-muted-foreground",
    icon: <CircleDashed />
  }
} satisfies Record<ReferralWorkflowStatus, {
  label: string;
  className: string;
  icon: ReactNode;
}>;
function ReferralWorkflowStepCard(t0) {
  const $ = _c(19);
  const {
    step
  } = t0;
  const status = STATUS_PRESENTATION[step.status];
  const isSkipped = step.status === "not_needed";
  const cardVariant = step.status === "automated" || step.status === "verified" ? "success" : step.status === "failed" ? "destructive" : step.status === "needs_action" || step.status === "verify" ? "warn" : "outline";
  let t1;
  if ($[0] !== step.continuation) {
    t1 = step.continuation ? <div className="space-y-3 border-t pt-4"><p className="text-sm text-muted-foreground">{step.continuation.instruction}</p><div className="flex justify-center">{step.continuation.control}</div></div> : null;
    $[0] = step.continuation;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== step.content || $[3] !== t1) {
    t2 = <>{step.content}{t1}</>;
    $[2] = step.content;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const body = t2;
  const t3 = `${step.number}. ${step.title}`;
  const t4 = step.status === "automated" ? undefined : status.icon;
  const t5 = step.status === "not_run" || isSkipped ? "bg-muted/20 text-muted-foreground opacity-40 grayscale" : undefined;
  let t6;
  if ($[5] !== status || $[6] !== step.status) {
    t6 = step.status === "automated" ? <span className="text-success-foreground" aria-label="Automated"><CheckCircleIcon weight="fill" className="size-5" /></span> : <Badge variant="outline" className={status.className}>{status.label}</Badge>;
    $[5] = status;
    $[6] = step.status;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== body || $[9] !== isSkipped || $[10] !== step.summary) {
    t7 = isSkipped ? <details><summary className="cursor-pointer text-sm text-muted-foreground">{step.summary}</summary><div className="mt-3 space-y-3">{body}</div></details> : <><p className="text-sm text-muted-foreground">{step.summary}</p>{body}</>;
    $[8] = body;
    $[9] = isSkipped;
    $[10] = step.summary;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== cardVariant || $[13] !== t3 || $[14] !== t4 || $[15] !== t5 || $[16] !== t6 || $[17] !== t7) {
    t8 = <ValerieCard title={t3} icon={t4} className={t5} variant={cardVariant} actions={t6} contentClassName="space-y-3">{t7}</ValerieCard>;
    $[12] = cardVariant;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
}
export function ReferralWorkflowTimeline(t0) {
  const $ = _c(12);
  const {
    steps,
    isProcessing: t1,
    waitingMessage,
    priorityContent,
    summaryContent
  } = t0;
  const isProcessing = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== isProcessing || $[1] !== priorityContent || $[2] !== steps || $[3] !== summaryContent || $[4] !== waitingMessage) {
    const numberedSteps = steps.map(_temp2);
    const actions = numberedSteps.filter(_temp3);
    const hasFailure = steps.some(_temp4);
    const summaryVariant = hasFailure ? "destructive" : actions.length || waitingMessage ? "warn" : isProcessing ? "outline" : "success";
    let t3;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <ListChecks />;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    const t4 = waitingMessage ? <p className="text-sm">{waitingMessage}</p> : actions.length ? <ol className="space-y-2 text-sm">{actions.map(_temp5)}</ol> : <p className="text-sm">{isProcessing ? "Automation is still running. This timeline will update as each durable workflow step completes." : "No manual actions remain."}</p>;
    let t5;
    if ($[7] !== priorityContent || $[8] !== summaryContent || $[9] !== summaryVariant || $[10] !== t4) {
      t5 = <ValerieCard title="Actions remaining" icon={t3} variant={summaryVariant} contentClassName="space-y-4">{priorityContent}{t4}{summaryContent}</ValerieCard>;
      $[7] = priorityContent;
      $[8] = summaryContent;
      $[9] = summaryVariant;
      $[10] = t4;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    t2 = <div className="space-y-8 pb-8">{t5}{numberedSteps.map(_temp6)}</div>;
    $[0] = isProcessing;
    $[1] = priorityContent;
    $[2] = steps;
    $[3] = summaryContent;
    $[4] = waitingMessage;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
function _temp6(step_3) {
  return <ReferralWorkflowStepCard key={step_3.number} step={step_3} />;
}
function _temp5(step_2) {
  return <li key={step_2.number}><strong>Step {step_2.number}: {step_2.title}</strong><span className="block text-muted-foreground">{step_2.summary}</span></li>;
}
function _temp4(step_1) {
  return step_1.status === "failed";
}
function _temp3(step_0) {
  return ["needs_action", "failed", "verify"].includes(step_0.status);
}
function _temp2(step, index) {
  return {
    ...step,
    number: index + 1
  };
}